import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { GlobalStyles } from '@experiences/theme';
import {
    UiAlertBanner,
    UiProgressButton,
    UiText,
} from '@experiences/ui-common';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { PortalAlertBar } from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

import UiForm from '../../common/UiForm';
import useExternalProvidersAADFormViewModel from './ExternalProvidersAADFormViewModel';

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    ...createStyles({
        input: { marginTop: 20 },
        inputLabel: {
            fontWeight: 600,
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        inputMargin: { marginBottom: '8px' },
        button: { marginTop: '24px' },
        errorIcon: {
            color: theme.palette.semantic.colorErrorIcon,
            marginRight: '4px',
        },
        errorMessage: {
            display: 'flex',
            flexGrow: 1,
            alignItems: 'center',
            fontSize: '14px',
            color: theme.palette.semantic.colorErrorIcon,
        },
        errorAlert: {
            width: 'fit-content',
            marginTop: '16px',
        },
        linkEmailsCheckbox: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        footer: {
            position: 'fixed',
            bottom: '0px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
            height: '64px',
            width: 'calc(100% - max(min(312px, 20vw), 175px) - 68px)',
            marginLeft: '-24px',
            padding: '0px 24px',
            backgroundColor: theme.palette.semantic.colorBackground,
            zIndex: 1,
        },
        footerButton: {
            color: theme.palette.semantic.colorPrimary,
            marginLeft: '8px',
            '& > a': {
                color: theme.palette.semantic.colorForegroundInverse,
                'text-decoration': 'none !important',
            },
        },
        centerLoader: {
            display: 'flex',
            justifyContent: 'center',
        },
        certMetdataBox: {
            backgroundColor: theme.palette.semantic.colorBackgroundSecondary,
            padding: '16px',
            marginBottom: '16px',
        },
        metdataDataDetails: {
            fontSize: '12px',
            lineHeight: '16px',
            fontWeight: 600,
            color: theme.palette.semantic.colorForegroundDeEmp,
            border: 'none',
            paddingTop: '0px',
            paddingRight: '24px',
            paddingLeft: '0px',
            paddingBottom: '12px',
        },
    }),
}));

const ExternalProvidersAADForm: React.FC<{
    onError: (error: any) => Promise<void> | void;
    onClose?: () => void;
    showButtonFooter?: boolean;
}> = ({
    onError,
    onClose,
    showButtonFooter = false,
}) => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();

    const DisableFeatureFedRamp = useFeatureFlagValue(Features.DisableFeatureFedRamp.name);
    const EnableAADClientCertificate = useFeatureFlagValue(Features.EnableAADClientCertificate.name);

    const {
        bulkAuthenticationSetting,
        testConnectionState,
        testConnectionError,
        emailLinkingChecked,
        setEmailLinkingChecked,
        methods: {
            register,
            handleSubmit,
            trigger,
            formState: {
                errors, isSubmitting,
            },
        },
        disableSubmit,
        disableTestConnection,
        cancel,
        handleAADAuthPopup,
        handleTestConnectionBypass,
        certMetadata,
    } = useExternalProvidersAADFormViewModel(
        showButtonFooter,
        onError,
        onClose,
    );

    if (!bulkAuthenticationSetting) {
        return <div className={classes.centerLoader}>
            <CircularProgress />
        </div>;
    }

    return (
        <>
            {showButtonFooter && !process.buildConfigs.disableTestConnection && testConnectionState === 'error' && (
                <div className={classes.errorAlert}>
                    <UiAlertBanner
                        closeable
                        type="error">
                        {`${translate({ id: 'CLIENT_AZURE_TEST_CONNECTION_FAILED' })}: ${testConnectionError}`}
                    </UiAlertBanner>
                </div>
            )}
            {DisableFeatureFedRamp && (
                <PortalAlertBar cancelable={false}>
                    <UiText>
                        {translate({ id: 'CLIENT_AZURE_AD_PUBLIC_INFO' })}
                    </UiText>
                            &nbsp;
                    <a
                        className={classes.a}
                        target="_blank"
                        rel="noopener noreferrer"
                        // eslint-disable-next-line max-len
                        href="https://docs.microsoft.com/en-us/azure/azure-government/documentation-government-plan-identity#choosing-your-identity-authority"
                    >
                        {translate({ id: 'CLIENT_MORE_INFO' })}
                    </a>
                </PortalAlertBar>
            )}
            <UiForm
                onSubmit={handleSubmit}>
                <div className={classes.input}>
                    <TextField
                        error={!!errors.tenantID}
                        label={translate({ id: 'CLIENT_AZURE_TENANT_ID' })}
                        placeholder={translate({ id: 'CLIENT_AZURE_TENANT_ID_PLACEHOLDER' })}
                        variant="outlined"
                        fullWidth
                        size="small"
                        inputProps={{
                            'data-cy': 'azure-tenant-id-field',
                            ...register('tenantID', {
                                required: translate(
                                    { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                    { 0: translate({ id: 'CLIENT_AZURE_TENANT_ID' }) },
                                ),
                            }),
                        }}
                        required
                    />
                </div>
                <div className={classes.input}>
                    <TextField
                        error={!!errors.applicationID}
                        label={translate({ id: 'CLIENT_AZURE_APPLICATION_ID' })}
                        placeholder={translate({ id: 'CLIENT_AZURE_APPLICATION_ID_PLACEHOLDER' })}
                        variant="outlined"
                        fullWidth
                        size="small"
                        inputProps={{
                            'data-cy': 'azure-application-id-field',
                            ...register('applicationID', {
                                required: translate(
                                    { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                    { 0: translate({ id: 'CLIENT_AZURE_APPLICATION_ID' }) },
                                ),
                            }),
                        }}
                        required
                    />
                </div>
                <div className={classes.input}>
                    <TextField
                        multiline={EnableAADClientCertificate}
                        maxRows={6}
                        error={!!errors.applicationSecret}
                        label={translate({
                            id: EnableAADClientCertificate
                                ? 'CLIENT_AZURE_APPLICATION_SECRET_CERTIFICATE'
                                : 'CLIENT_AZURE_APPLICATION_SECRET',
                        })}
                        placeholder={!EnableAADClientCertificate
                            ? translate({ id: 'CLIENT_AZURE_APPLICATION_SECRET_PLACEHOLDER' })
                            : undefined}
                        helperText={EnableAADClientCertificate
                            ? translate({ id: 'CLIENT_AZURE_APPLICATION_SECRET_CERTIFICATE_PLACEHOLDER' })
                            : undefined}
                        variant="outlined"
                        autoComplete="off"
                        fullWidth
                        size="small"
                        inputProps={{
                            'data-cy': 'azure-application-secret-field',
                            ...register('applicationSecret', {
                                required: translate(
                                    { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                    { 0: translate({ id: 'CLIENT_AZURE_APPLICATION_SECRET' }) },
                                ),
                            }),
                        }}
                        style={{ marginBottom: '20px' }}
                        required
                    />
                    {EnableAADClientCertificate && certMetadata && <Box className={classes.certMetdataBox}>
                        <UiText className={classes.metdataDataDetails}>
                            {translate({ id: 'CLIENT_CERTIFICATE_DETAILS' })}
                        </UiText>
                        <TableContainer>
                            <Table style={{ tableLayout: 'fixed' }}>
                                <TableRow>
                                    <TableCell
                                        className={classes.metdataDataDetails}
                                        width="20%">
                                        {`${translate({ id: 'CLIENT_THUMBPRINT' })}:`}
                                    </TableCell>
                                    <TableCell
                                        className={classes.metdataDataDetails}
                                        width="80%">
                                        {certMetadata.Thumbprint}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        className={classes.metdataDataDetails}
                                        width="20%">
                                        {`${translate({ id: 'CLIENT_SUBJECT_CN' })}:`}
                                    </TableCell>
                                    <TableCell
                                        className={classes.metdataDataDetails}
                                        width="80%">
                                        {certMetadata.Subject}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        className={classes.metdataDataDetails}
                                        width="20%">
                                        {`${translate({ id: 'CLIENT_START_DATE' })}:`}
                                    </TableCell>
                                    <TableCell
                                        className={classes.metdataDataDetails}
                                        width="80%">
                                        {certMetadata['Start date']?.toString()}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        className={classes.metdataDataDetails}
                                        width="20%">
                                        {`${translate({ id: 'CLIENT_EXPIRES_ON_CERT' })}:`}
                                    </TableCell>
                                    <TableCell
                                        className={classes.metdataDataDetails}
                                        width="80%">
                                        {certMetadata.Expires?.toString()}
                                    </TableCell>
                                </TableRow>
                            </Table>
                        </TableContainer>
                    </Box>}
                </div>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={emailLinkingChecked}
                            onChange={async e => {
                                setEmailLinkingChecked(e.target.checked);
                                await trigger();
                            }}
                            data-cy="link-email-checkbox"
                        />
                    }
                    label={
                        <div className={classes.linkEmailsCheckbox}>
                            <UiText>
                                {translate({
                                    id: process.buildConfigs.disableUserInvite
                                        ? 'CLIENT_AZURE_AD_CHECK_LINK_EMAILS_ADD'
                                        : 'CLIENT_AZURE_AD_CHECK_LINK_EMAILS_INVITE',
                                })}
                            </UiText>
                        </div>
                    }
                />
                {!showButtonFooter
                    ? (
                        <div>
                            {!process.buildConfigs.disableTestConnection && (
                                <UiProgressButton
                                    className={classes.button}
                                    component={Link}
                                    disabled={disableTestConnection}
                                    target="popup"
                                    onClick={handleAADAuthPopup}
                                    loading={testConnectionState === 'loading'}
                                    endIcon={testConnectionState === 'success' && <CheckIcon />}
                                    data-cy="azure-test-connection-button"
                                >
                                    {translate({ id: 'CLIENT_AZURE_TEST_CONNECTION' })}
                                </UiProgressButton>
                            )}
                            {!process.buildConfigs.disableTestConnection &&
                                (testConnectionState === 'error' || testConnectionState === 'unknown') && (
                                <div className={classes.errorMessage}>
                                    <ErrorIcon className={classes.errorIcon} />
                                    <p>
                                        {`${translate({ id: 'CLIENT_AZURE_TEST_CONNECTION_FAILED' })}: ${testConnectionError}`}
                                        {process.buildConfigs.enableTestConnectionBypass && testConnectionState === 'unknown' && <>
                                            &nbsp;
                                            {translate({ id: 'CLIENT_RELEASE_USER_LICENSE_DETAILS_P3' })}
                                            &nbsp;
                                            <Link
                                                component='p'
                                                underline='hover'
                                                display="inline"
                                                onClick={handleTestConnectionBypass}
                                            >
                                                {translate({ id: 'CLIENT_CLICK_HERE' })}
                                            </Link>
                                        </>}
                                    </p>
                                </div>
                            )}
                            <UiProgressButton
                                type="submit"
                                loading={isSubmitting}
                                disabled={disableSubmit}
                                variant="contained"
                                className={classes.button}
                                data-cy="azure-submit-button"
                            >
                                {translate({ id: 'CLIENT_SAVE' })}
                            </UiProgressButton>
                        </div>
                    )
                    : (
                        <div className={classes.footer}>
                            <Button
                                className={classes.footerButton}
                                onClick={cancel}>
                                {translate({ id: 'CLIENT_CANCEL' })}
                            </Button>
                            <UiProgressButton
                                className={classes.footerButton}
                                component={Link}
                                loading={testConnectionState === 'loading' || isSubmitting}
                                disabled={disableTestConnection}
                                target="popup"
                                variant="contained"
                                onClick={handleAADAuthPopup}
                                data-cy="azure-test-and-save-button"
                            >
                                {translate({ id: 'CLIENT_TEST_AND_SAVE' })}
                            </UiProgressButton>
                        </div>
                    )}
            </UiForm>
        </>
    );
};

export default ExternalProvidersAADForm;
